import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import { AlertContent, H3, ModalFactory, Text } from 'components';
import { appConfig } from 'config/app';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectIsTipCalcFormDirty } from 'store/App';
import { CalculatorRules, getCalculatorRulesRequest, selectCalculatorRules } from 'store/CalculatorRules';
import { getJobTitlesRequest, selectJobTitlesBySite } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { SiteJobSource, TipCalculationsTab } from 'types';
import TipCalculatorSiteSelector from '../../../components/SiteSelector/TipCalculatorSiteSelector';
import { SectionTitle, SettingsContent, SettingsContentLayout, SettingsPageHeader, SettingsSection, SettingsSectionContent, SettingsSectionHeader } from '../../Settings/components';
import { DisabledWrapper, LoadingSkeleton, StyledLink } from '../styles';
import { CreditCardFees } from './sections/CreditCardFees';
import { EmployeeOnboarding } from './sections/EmployeeOnboarding';
import { GenerateHealthReport } from './sections/HealthReport';
import { ImportExport } from './sections/ImportExport';
import { SalesGroupsForm } from './sections/SalesGroups';
import { SuggestRules } from './sections/SuggestRules';
import { TipCalculationsForm } from './sections/TipCalculations';

interface IParams {
  tab?: TipCalculationsTab;
}

const getActiveTab = (tab: TipCalculationsTab) => {
  let activeTab = TipCalculationsTab.TIP_SHARES;
  if (Object.values(TipCalculationsTab).includes(tab)) {
    activeTab = tab;
  }
  return activeTab;
};

const CalculatorSettings = () => {
  const dispatch = useDispatch();
  const { tab } = useParams<IParams>();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const jobTitles = useSelector(selectJobTitlesBySite(currentSite?.id));
  const isLoadingCalculatorRules = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GET_CALCULATOR_RULES_REQUEST));
  const isLoading = isLoadingCalculatorRules || !jobTitles || (currentCalculatorRules?.siteId && currentCalculatorRules.siteId !== currentSite?.id);
  const isTipCalcFormDirty = useSelector(selectIsTipCalcFormDirty);

  useEffect(() => {
    if (currentSite?.id && currentSite.settings?.tipCalculator) {
      dispatch(getCalculatorRulesRequest(currentSite.id));
      dispatch(getJobTitlesRequest({ siteId: currentSite.id, sources: [SiteJobSource.POS] }));
    }
  }, [currentSite]);

  const tipCalculatorContent = () => {
    let content = <LoadingSkeleton />;

    if (currentSite) {
      if (!currentSite.settings?.tipCalculator) {
        content = (
          <DisabledWrapper>
            <Text fontWeight="semibold">Tip Calculations Not Enabled</Text>
            <Text size="small">
              {'The site selected is not enabled for tip calculations. '}
              <StyledLink target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.SUPPORT}>
                Contact Kickfin Support
              </StyledLink>
              {' to learn more.'}
            </Text>
          </DisabledWrapper>
        );
      } else if (!isLoading) {
        content = (
          <>
            <SalesGroupsForm calculatorRules={currentCalculatorRules} />
            <CreditCardFees currentSite={currentSite} />
            <TipCalculationsForm calculatorRules={currentCalculatorRules} activeTab={getActiveTab(tab)} />
            <ImportExport calculatorRules={currentCalculatorRules} />
            <EmployeeOnboarding />
            <SuggestRules />
            <GenerateHealthReport />
          </>
        );
      }
    }
    return content;
  };

  return (
    <>
      <SettingsContent data-cy="calculator-settings-container">
        <SettingsPageHeader />

        <SettingsContentLayout>
          <SettingsSection>
            <SettingsSectionHeader>
              <H3>Calculator Settings</H3>
            </SettingsSectionHeader>

            <SettingsSectionContent light data-cy="calculator-settings-form">
              <SectionTitle>Select Site</SectionTitle>
              <TipCalculatorSiteSelector />
              {tipCalculatorContent()}
            </SettingsSectionContent>
          </SettingsSection>
        </SettingsContentLayout>
      </SettingsContent>

      <NavigationPrompt when={isTipCalcFormDirty}>
        {({ onConfirm, onCancel }) => (
          <ModalFactory customModal onClose={onCancel} isActive closeOnOutSideClick={false}>
            <AlertContent
              title="Warning"
              message="You have unsaved changes that will be lost if you navigate away from this page. Are you sure you want to proceed?"
              leftActionLabel="Cancel"
              rightActionLabel="Proceed"
              onClose={onCancel}
              onLeftAction={onCancel}
              onRightAction={onConfirm}
              maxWidth={420}
            />
          </ModalFactory>
        )}
      </NavigationPrompt>
    </>
  );
};

export default CalculatorSettings;
